import React, { useEffect, useState } from 'react';
import { Button, Drawer, makeStyles, Tooltip } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import SettingsOptionIcon from '@material-ui/icons/Settings';
import InfoIcon from '@mui/icons-material/Info';
import {
  HS_DIGITAL_CHECKIN,
  HS_ROOM_RESERVATION,
  HS_SPA_INROOM_DINING,
  HS_SPA_RESERVATION,
  HS_RESTAURANT_RESERVATION,
  HS_TELEPHONY,
  HS_HOUSEKEEPING,
  HS_DIGITAL_CHECKOUT,
  HS_LOYALTY,
  HS_PAYMENT,
  HS_WEATHER,
  HS_KYC_DOC,
  HS_TS,
  HS_CHAT,
  HS_EVENTHUB,
  HS_ANALYTICS,
  HS_TPNS,
  HS_CRS,
} from '../../../../../constants/HotelServicesConst';
import RoomReservation from './room-reservation/Room-Reservation';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { selectedHotelSelector } from '../../../../property/propertySlice';
import DigitalCheckin from './digital-checkin/Digital-Checkin';
import SpaReservation from './spa-reservation/Spa-Reservation';
import InRoomDining from './inroom-dining/InRoom-Dining';
import './HotelService.scss';
import {
  fetchDigitalCheckinConfigs,
  fetchIntegrationConfigs,
  fetchIntegrationConfiguration,
  fetchROomReservationConfig,
  hotelSystemSettingsSelector,
} from '../../../configurationSlice';
import RestaurantReservation from './restaurant-reservation/Restaurant-reservation';
import TelephonyConfigurator from './telephony-configurator/Telephony-Configurator';
import Housekeeping from './housekeeping/Housekeeping';

import SettingsScreen from './settings-option/SettingsScreen';
import DigitalCheckout from './digital-checkout/Digital-Checkout';
import Chat from './chat/Chat';
import Weather from './weather/Weather';
import Payment from './payment/Payment';
import Loyalty from './loyalty/Loyalty';
import TravelService from './travel-service/TravelService';
import EventHub from './event-hub/EventHub';
import Analytics from './analytics/Analytics';
import ThirdPartyNotificationService from './third-party-notification-service/ThirdPartyNotificationService';
import CentralReservationSystem from './crs/CentralReservationSystem';
import DocumentScan from './document-sacn/DocumentScan';

const useStyles = makeStyles((theme) => ({
  activeButton: {
    margin: '5px !important',
    borderRadius: '5px !important',
    borderBottom: '4px solid #74E287',
    opacity: 1,
    minWidth: '100px',
    fontWeight: 500,
    fontSize: '14px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  button: {
    border: '1px solid #999999',
    margin: '5px !important',
    borderRadius: '5px !important',
    minWidth: '100px',
    fontWeight: 500,
    fontSize: '14px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  input: {
    display: 'none',
  },
}));

const tabDescriptions = {
  [HS_ROOM_RESERVATION]: "Configure the hotel's PMS integration for reservations and guest booking. Set vendor-specific endpoints, credentials, hotel codes, and field mappings.",
  [HS_DIGITAL_CHECKIN]: "Configure digital key / door lock vendor integration used for guest access flows. Set property identifiers, device/site mapping, API keys.",
  [HS_DIGITAL_CHECKOUT]: "Digital Check-Out is an optional integration used to enable guests to complete check-out digitally.",
  [HS_RESTAURANT_RESERVATION]: "Configure restaurant table reservation provider integration. Set venue/outlet mapping, booking rules, and API credentials for availability and reservation actions.",
  [HS_SPA_RESERVATION]: "Configure the spa booking vendor integration. Set services, therapist/slot rules, pricing mappings, and vendor credentials for booking/cancel flows.",
  [HS_SPA_INROOM_DINING]: "Configure IRD/POS vendor integration for menu sync and order placement. Set outlet/store mapping, menu & modifier rules, taxes/charges mapping, and order status callbacks.",
  [HS_HOUSEKEEPING]: "Configure housekeeping / service request integration vendor. Set request categories, routing rules, SLA/status mapping, and vendor credentials for ticket creation and updates.",
  [HS_TELEPHONY]: "Configure the EPBX/telephony integration.",
  [HS_LOYALTY]: "Configure the loyalty/CRM provider integration. Set url, api key and authentication credentials.",
  [HS_PAYMENT]: "Configure the payment gateway integration. Set merchant keys, supported payment methods, and credentials.",
  [HS_WEATHER]: "Configure the weather provider used for property/location forecasts. Set API key, location default etc.",
  [HS_KYC_DOC]: "Configure the document scanning / OCR / KYC vendor integration. Set API keys, supported document types, validation rules, and verification workflow settings.",
  [HS_TS]: "Configure Travel Desk (TD) integration for concierge travel services. Set vendor endpoints, credentials.",
  [HS_CHAT]: "Configure chat and whatsapp messaging integrations including vendor credentials, webhook callbacks, templates.",
  [HS_EVENTHUB]: "Configure the event management integration used to publish and manage hotel events. Set vendor credentials, organizer settings, and event sync rules.",
  [HS_ANALYTICS]: "Configure Google Analytics integration for tracking product usage and events. Set vendor credentials.",
  [HS_TPNS]: "Configure Marketing Cloud for push notifications and messaging. Set API credentials, app/channel configuration, templates, and delivery tracking settings.",
};

const HotelService = (props) => {
  const { showLoader = () => {} } = props;
  const [activeTab, setActiveTab] = useState(HS_ROOM_RESERVATION);
  const classes = useStyles();
  const selectedHotel = useSelector(selectedHotelSelector);
  const systemSettings = useSelector(hotelSystemSettingsSelector);
  const [state, setState] = useState({
    status: false,
  });
  const [hotelSystemSettings, setHotelSystemsSettings] = useState([]);

  useEffect(() => {
    if (systemSettings) {
      setHotelSystemsSettings(systemSettings);
    }
  }, [systemSettings]);

  useEffect(() => {
    if (systemSettings) {
      if (systemSettings.roomReservations) {
        setActiveTab(HS_ROOM_RESERVATION);
        return;
      }
      if (systemSettings.digitalCheckIn) {
        setActiveTab(HS_DIGITAL_CHECKIN);
        return;
      }
      if (systemSettings.digitalCheckOut) {
        setActiveTab(HS_DIGITAL_CHECKOUT);
        return;
      }
      if (systemSettings.tableReservation) {
        setActiveTab(HS_RESTAURANT_RESERVATION);
        return;
      }
      if (systemSettings.spaReservation) {
        setActiveTab(HS_SPA_RESERVATION);
        return;
      }
      if (systemSettings.inRoomDinning) {
        setActiveTab(HS_SPA_INROOM_DINING);
        return;
      }
      if (systemSettings.housekeeping) {
        setActiveTab(HS_HOUSEKEEPING);
        return;
      }
      if (systemSettings.telephony) {
        setActiveTab(HS_TELEPHONY);
        return;
      }
      if (systemSettings.loyalty) {
        setActiveTab(HS_LOYALTY);
        return;
      }
      if (systemSettings.payment) {
        setActiveTab(HS_PAYMENT);
        return;
      }
      if (systemSettings.weather) {
        setActiveTab(HS_WEATHER);
        return;
      }
      if (systemSettings.documentScan) {
        setActiveTab(HS_KYC_DOC);
        return;
      }
      if (systemSettings.travelService) {
        setActiveTab(HS_TS);
        return;
      }
      if (systemSettings.chat) {
        setActiveTab(HS_CHAT);
        return;
      }
      if (systemSettings.eventHub) {
        setActiveTab(HS_EVENTHUB);
        return;
      }
      if (systemSettings.analytics) {
        setActiveTab(HS_ANALYTICS);
        return;
      }
      if (systemSettings.thirdPartyNotificationService) {
        setActiveTab(HS_TPNS);
        return;
      }
      // if (systemSettings.centralReservationSystem) {
      //   setActiveTab(HS_CRS);
      //   return;
      // }
    }
  }, [systemSettings]);

  const onDrawerToggle = (status) => {
    setState({ status: status });
  };

  const toggleDrawer = (open) => (event, reason) => {
    if (
      reason === 'backdropClick' ||
      (event?.type === 'keydown' && (event?.key === 'Tab' || event?.key === 'Shift'))
    ) {
      return;
    }
    setState(open);
  };

  return (
    <>
      <Drawer
        anchor={'right'}
        open={state.status}
        onClose={toggleDrawer(false)}
        className='right-drawer'
      >
        <SettingsScreen
          toggleDrawer={onDrawerToggle}
          currentObj={hotelSystemSettings}
          selectedHotel={selectedHotel}
          showLoader={showLoader}
        />
      </Drawer>
      <div className='container mt-4 pb-6'>
        <div className='mt-5 row'>
          <div class='col'>
            <div className='col-12 px-0 d-flex align-items-center'>
              <span className='page-title text-nowrap'>Hotel Services</span>
            </div>
          </div>
          <div class='col'>
            <div className='hotel-service-setting-btn'>
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                startIcon={<SettingsOptionIcon />}
                onClick={(e) => {
                  onDrawerToggle(true);
                }}
                className='me-5'
              >
                Settings
              </Button>
            </div>
          </div>
        </div>
        <div className='row mx-2 d-flex mt-4'>
          <div className='card shadow'>
            <div className='row d-flex pt-4 hotel-reservation p-3'>
              <div className='d-flex flex-wrap'>
                {hotelSystemSettings.roomReservations && (
                  <Button
                    size='large'
                    variant={activeTab === HS_ROOM_RESERVATION ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_ROOM_RESERVATION ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_ROOM_RESERVATION)}
                  >
                    Room Reservation
                  </Button>
                )}

                {hotelSystemSettings.digitalCheckIn && (
                  <Button
                    size='large'
                    variant={activeTab === HS_DIGITAL_CHECKIN ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_DIGITAL_CHECKIN ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_DIGITAL_CHECKIN)}
                  >
                    Digital Check-In
                  </Button>
                )}

                {hotelSystemSettings.digitalCheckOut && (
                  <Button
                    size='large'
                    variant={activeTab === HS_DIGITAL_CHECKOUT ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_DIGITAL_CHECKOUT ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_DIGITAL_CHECKOUT)}
                  >
                    Digital Check-Out
                  </Button>
                )}

                {hotelSystemSettings.tableReservation && (
                  <Button
                    size='large'
                    variant={activeTab === HS_RESTAURANT_RESERVATION ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_RESTAURANT_RESERVATION
                        ? classes.activeButton
                        : classes.button
                    }
                    onClick={() => setActiveTab(HS_RESTAURANT_RESERVATION)}
                  >
                    Table Reservation
                  </Button>
                )}

                {hotelSystemSettings.spaReservation && (
                  <Button
                    size='large'
                    variant={activeTab === HS_SPA_RESERVATION ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_SPA_RESERVATION ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_SPA_RESERVATION)}
                  >
                    Spa Reservation
                  </Button>
                )}

                {hotelSystemSettings.inRoomDinning && (
                  <Button
                    size='large'
                    variant={activeTab === HS_SPA_INROOM_DINING ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_SPA_INROOM_DINING ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_SPA_INROOM_DINING)}
                  >
                    In-Room Dining
                  </Button>
                )}

                {hotelSystemSettings.housekeeping && (
                  <Button
                    size='large'
                    variant={activeTab === HS_HOUSEKEEPING ? 'outlined' : 'text'}
                    className={
                      activeTab === HS_HOUSEKEEPING ? classes.activeButton : classes.button
                    }
                    onClick={() => setActiveTab(HS_HOUSEKEEPING)}
                  >
                    Housekeeping
                  </Button>
                )}

                {hotelSystemSettings.telephony && (
                  <Button
                    size='large'
                    variant={activeTab === HS_TELEPHONY ? 'outlined' : 'text'}
                    className={activeTab === HS_TELEPHONY ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_TELEPHONY)}
                  >
                    Telephony
                  </Button>
                )}
                {hotelSystemSettings.loyalty && (
                  <Button
                    size='large'
                    variant={activeTab === HS_LOYALTY ? 'outlined' : 'text'}
                    className={activeTab === HS_LOYALTY ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_LOYALTY)}
                  >
                    Loyalty
                  </Button>
                )}

                {hotelSystemSettings.payment && (
                  <Button
                    size='large'
                    variant={activeTab === HS_PAYMENT ? 'outlined' : 'text'}
                    className={activeTab === HS_PAYMENT ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_PAYMENT)}
                  >
                    Payment
                  </Button>
                )}

                {hotelSystemSettings.weather && (
                  <Button
                    size='large'
                    variant={activeTab === HS_WEATHER ? 'outlined' : 'text'}
                    className={activeTab === HS_WEATHER ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_WEATHER)}
                  >
                    Weather
                  </Button>
                )}

                {hotelSystemSettings.documentScan && (
                  <Button
                    size='large'
                    variant={activeTab === HS_KYC_DOC ? 'outlined' : 'text'}
                    className={activeTab === HS_KYC_DOC ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_KYC_DOC)}
                  >
                    Document Scan
                  </Button>
                )}

                {hotelSystemSettings.travelService && (
                  <Button
                    size='large'
                    variant={activeTab === HS_TS ? 'outlined' : 'text'}
                    className={activeTab === HS_TS ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_TS)}
                  >
                    Travel Service
                  </Button>
                )}

                {hotelSystemSettings.chat && (
                  <Button
                    size='large'
                    variant={activeTab === HS_CHAT ? 'outlined' : 'text'}
                    className={activeTab === HS_CHAT ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_CHAT)}
                  >
                    CHAT
                  </Button>
                )}
                {hotelSystemSettings.eventHub && (
                  <Button
                    size='large'
                    variant={activeTab === HS_EVENTHUB ? 'outlined' : 'text'}
                    className={activeTab === HS_EVENTHUB ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_EVENTHUB)}
                  >
                    Event Hub
                  </Button>
                )}
                {hotelSystemSettings.analytics && (
                  <Button
                    size='large'
                    variant={activeTab === HS_ANALYTICS ? 'outlined' : 'text'}
                    className={activeTab === HS_ANALYTICS ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_ANALYTICS)}
                  >
                    Analytics
                  </Button>
                )}
                {hotelSystemSettings.thirdPartyNotificationService && (
                  <Button
                    size='large'
                    variant={activeTab === HS_TPNS ? 'outlined' : 'text'}
                    className={activeTab === HS_TPNS ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_TPNS)}
                  >
                    TPNS
                  </Button>
                )}
                {/* {hotelSystemSettings.centralReservationSystem && (
                  <Button
                    size='large'
                    variant={activeTab === HS_CRS ? 'outlined' : 'text'}
                    className={activeTab === HS_CRS ? classes.activeButton : classes.button}
                    onClick={() => setActiveTab(HS_CRS)}
                  >
                    CRS
                  </Button>
                )} */}
              </div>
            </div>
            {tabDescriptions[activeTab] && (
                <div className='row mx-2 mt-2 mb-1 d-flex align-items-center'>
                  <div className='d-flex align-items-center'>
                    <span className='fw-bold ms-2'>More info</span>
                    <span className='ms-2'><Tooltip 
                      title={tabDescriptions[activeTab]} 
                      arrow
                      placement="right"
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <InfoIcon className={classes.infoIcon} />
                    </Tooltip></span>                  
                  </div>
                </div>
              )}
            <div className='row mx-2 mt-2 hotel-service-config'>
              {activeTab === HS_ROOM_RESERVATION && hotelSystemSettings?.roomReservations && (
                <RoomReservation selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_DIGITAL_CHECKIN && hotelSystemSettings?.digitalCheckIn && (
                <DigitalCheckin selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_DIGITAL_CHECKOUT && hotelSystemSettings?.digitalCheckOut && (
                <DigitalCheckout selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_SPA_RESERVATION && hotelSystemSettings?.spaReservation && (
                <SpaReservation selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_SPA_INROOM_DINING && hotelSystemSettings?.inRoomDinning && (
                <InRoomDining selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_RESTAURANT_RESERVATION && hotelSystemSettings?.tableReservation && (
                <RestaurantReservation selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_HOUSEKEEPING && hotelSystemSettings?.housekeeping && (
                <Housekeeping selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_TELEPHONY && hotelSystemSettings?.telephony && (
                <TelephonyConfigurator selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_LOYALTY && hotelSystemSettings?.loyalty && (
                <Loyalty selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_PAYMENT && hotelSystemSettings?.payment && (
                <Payment selectedHotel={selectedHotel} showLoader={showLoader} />
              )}

              {activeTab === HS_WEATHER && hotelSystemSettings?.weather && (
                <Weather selectedHotel={selectedHotel} showLoader={showLoader} />
              )}

              {activeTab === HS_KYC_DOC && hotelSystemSettings?.documentScan && (
                <DocumentScan selectedHotel={selectedHotel} showLoader={showLoader} />
              )}

              {activeTab === HS_TS && hotelSystemSettings?.travelService && (
                <TravelService selectedHotel={selectedHotel} showLoader={showLoader} />
              )}

              {activeTab === HS_CHAT && hotelSystemSettings?.chat && (
                <Chat selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_EVENTHUB && hotelSystemSettings?.eventHub && (
                <EventHub selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_ANALYTICS && hotelSystemSettings?.analytics && (
                <Analytics selectedHotel={selectedHotel} showLoader={showLoader} />
              )}
              {activeTab === HS_TPNS && hotelSystemSettings?.thirdPartyNotificationService && (
                <ThirdPartyNotificationService
                  selectedHotel={selectedHotel}
                  showLoader={showLoader}
                />
              )}
              {/* {activeTab === HS_CRS && hotelSystemSettings?.centralReservationSystem && (
                <CentralReservationSystem selectedHotel={selectedHotel} showLoader={showLoader} />
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HotelService;
